import { MUSICS_LIBRARY } from "../library/musicsLibrary.js";
import { getAuriaIntent } from "../domain/intents.js";
import {
  learnedContribution,
  seededUnit,
  weightedPick,
} from "./learningScore.js";

export const SESSION_TARGETS = Object.freeze({
  "30sec": 30,
  "2min": 120,
  "5min": 300,
  "10min": 600,
});

export const CROSSFADE_SECONDS = 4;
const MIN_SEGMENT_SECONDS = 20;

function maxArcTracks(target) {
  if (target <= 120) return 2;
  if (target <= 300) return 3;
  return 4;
}

/* Un pool peut contenir des morceaux sans pouvoir couvrir la cible : masquer
   quelques titres sur un objectif peu fourni suffit à faire échouer l'arc.
   La capacité réelle est donc mesurée avant de décider du repli. */
function poolCoversTarget(pool, target) {
  const durations = pool
    .map((track) => Number(track.duration))
    .filter((duration) => Number.isFinite(duration) && duration > 0)
    .sort((a, b) => b - a)
    .slice(0, maxArcTracks(target));

  if (!durations.length) return false;
  if (durations[0] >= target) return true;

  const capacity = durations.reduce((sum, duration, index) => sum + duration - (index ? CROSSFADE_SECONDS : 0), 0);
  return capacity >= target - MIN_SEGMENT_SECONDS;
}

function energyMatch(energy, range) {
  const value = Number(energy);
  const [minimum, maximum] = range;

  if (!Number.isFinite(value)) return 0.5;
  if (value >= minimum && value <= maximum) return 1;

  const distance = value < minimum ? minimum - value : value - maximum;
  if (distance === 1) return 0.55;
  if (distance === 2) return 0.25;
  return 0.1;
}

function scoreTrack({
  track,
  intent,
  seed,
  recentTrackIds,
  favoriteTrackIds,
  preferences,
}) {
  const intentConfig = getAuriaIntent(intent);
  if (!intentConfig) return { track, score: Number.NEGATIVE_INFINITY };

  let score = 100;
  score += energyMatch(track.energy, intentConfig.energyRange) * 34;

  const recentIndex = recentTrackIds.indexOf(track.id);
  if (recentIndex === 0) score -= 34;
  else if (recentIndex === 1) score -= 22;
  else if (recentIndex >= 2) score -= 10;

  if (favoriteTrackIds.includes(track.id)) score += 10;

  const stat = preferences?.learning?.tracks?.[intent]?.[track.id];
  score += learnedContribution(stat, 12);
  score += seededUnit(seed, track.id) * 9;

  return { track, score };
}

function chooseDominantTuning(scored) {
  const totals = new Map();

  for (const item of scored) {
    const key = String(item.track.tuning ?? "default");
    totals.set(key, (totals.get(key) || 0) + Math.max(1, item.score));
  }

  return [...totals.entries()].sort((a, b) => b[1] - a[1])[0]?.[0] || null;
}

function singleTrackArc(scored, target, seed) {
  const eligible = scored.filter(
    ({ track }) => Number(track.duration) >= target,
  );

  if (!eligible.length) return null;

  const selected = weightedPick(eligible, seed, 5)?.track;
  if (!selected) return null;

  return {
    tracks: [{ ...selected, playUntil: target }],
    totalDuration: target,
    arcKind: Number(selected.duration) > target ? "single-truncated" : "single",
  };
}

function roleScore(track, desiredRole, position, totalPositions) {
  const roles = Array.isArray(track.arcRole) ? track.arcRole : ["core"];
  let score = roles.includes(desiredRole) ? 30 : 0;

  const energy = Number(track.energy) || 2;
  if (desiredRole === "intro") score += Math.max(0, 8 - energy * 2);
  if (desiredRole === "closure") score += Math.max(0, 10 - energy * 2.5);
  if (desiredRole === "core") score += Math.min(10, energy * 2);

  const progress = totalPositions <= 1 ? 0 : position / (totalPositions - 1);
  if (progress > 0.65 && desiredRole === "closure") score += 8;

  return score;
}

function stackedArc(scored, target, seed) {
  if (!scored.length || target <= 30) return null;

  const chosen = [];
  let total = 0;
  const maximumTracks = maxArcTracks(target);
  const roles =
    maximumTracks === 2
      ? ["intro", "closure"]
      : maximumTracks === 3
        ? ["intro", "core", "closure"]
        : ["intro", "core", "core", "closure"];

  for (
    let position = 0;
    position < maximumTracks && total < target - MIN_SEGMENT_SECONDS;
    position += 1
  ) {
    const role = roles[position];
    const available = scored
      .filter(({ track }) => !chosen.some((item) => item.id === track.id))
      .map((item) => ({
        ...item,
        score:
          item.score +
          roleScore(item.track, role, position, maximumTracks) +
          seededUnit(seed + position, `${item.track.id}:${role}`) * 5,
      }))
      .sort((a, b) => b.score - a.score);

    const selected = weightedPick(available, seed + position * 97, 4)?.track;
    if (!selected) break;

    const selectedDuration = Number(selected.duration);
    if (!Number.isFinite(selectedDuration) || selectedDuration <= 0) continue;

    const overlap = chosen.length ? CROSSFADE_SECONDS : 0;
    const remaining = target - total;

    /* Le dernier morceau est coupé exactement sur la cible, jamais rogné après coup. */
    if (selectedDuration - overlap >= remaining) {
      chosen.push({ ...selected, playUntil: Math.min(selectedDuration, remaining + overlap) });
      total = target;
      break;
    }

    chosen.push({ ...selected, playUntil: selectedDuration });
    total += selectedDuration - overlap;
  }

  if (!chosen.length || total < target - MIN_SEGMENT_SECONDS) return null;

  const exactTotal = chosen.reduce(
    (sum, track, index) =>
      sum +
      (index === 0
        ? track.playUntil
        : Math.max(0, track.playUntil - CROSSFADE_SECONDS)),
    0,
  );

  return {
    tracks: chosen,
    totalDuration: Math.min(target, Math.round(exactTotal)),
    arcKind: chosen.length === 1 ? "single" : `arc-${chosen.length}`,
  };
}

export function buildSessionArc({
  intent,
  durationKey,
  recentTrackIds = [],
  hiddenTrackIds = [],
  favoriteTrackIds = [],
  excludeTrackIds = [],
  seed = 0,
  preferences = {},
}) {
  const target = SESSION_TARGETS[durationKey];
  if (!target || !getAuriaIntent(intent)) return null;

  const hidden = new Set([
    ...(hiddenTrackIds || []),
    ...(excludeTrackIds || []),
  ]);

  const intentPool = MUSICS_LIBRARY.filter((track) => track.intent === intent);
  if (!intentPool.length) return null;

  /* Les morceaux masqués ne rendent jamais un objectif incomposable : dès que
     le pool restant ne couvre plus la cible, la préférence est relâchée. */
  const filtered = intentPool.filter((track) => !hidden.has(track.id));
  const pool = poolCoversTarget(filtered, target) ? filtered : intentPool;

  const scored = pool
    .map((track) =>
      scoreTrack({
        track,
        intent,
        seed,
        recentTrackIds,
        favoriteTrackIds,
        preferences,
      }),
    )
    .filter((item) => Number.isFinite(item.score))
    .sort((a, b) => b.score - a.score);

  if (!scored.length) return null;

  const dominantTuning = chooseDominantTuning(scored);
  const coherent = dominantTuning
    ? scored.filter(
        ({ track }) => String(track.tuning ?? "default") === dominantTuning,
      )
    : scored;

  /*
   * Une session de 30 secondes reste toujours un extrait d'un seul morceau.
   * Il n'y a donc ni empilement ni crossfade à cette durée.
   */
  if (target === 30) {
    return (
      singleTrackArc(coherent, target, seed) ||
      singleTrackArc(scored, target, seed)
    );
  }

  /*
   * Un morceau suffisamment long reste prioritaire. Le player l'arrête
   * exactement à la cible avec son fondu final. Si aucun morceau ne suffit,
   * un arc cohérent est construit avec des crossfades.
   */
  return (
    singleTrackArc(coherent, target, seed) ||
    stackedArc(coherent, target, seed) ||
    singleTrackArc(scored, target, seed) ||
    stackedArc(scored, target, seed)
  );
}
